//@ts-nocheck
import { CheckCircleIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { getSnapshot } from "mobx-state-tree";
import React, { useState } from "react";
import Dropzone from "react-dropzone";
import Icon, { XButton } from "../../components/ui-elements/Icon";
import Upload from "../../components/Upload";
import { IIdentityStore } from "../../stores";
import { ImageUploadHandler } from "./ImageUploadHandler";

const placeholder =
  "https://res.cloudinary.com/tuteria/image/upload/v1675475738/landingpage/Avatar.png";

export const ImagePreview: React.FC<{
  isLoading?: boolean;
  image?: any;
  imageSize?: string;
  rounded?: string;
  hasError?: string;
  imagePlaceholder?: string;
  className?: string;
  [key: string]: any;
}> = ({
  isLoading,
  image,
  imageSize = "232px",
  rounded = "md",
  imagePlaceholder = placeholder,
  className = "",
  ...rest
}) => (
  <Flex
    className={`ImagePreview ${className}`.trim()}
    flexShrink={0}
    align="center"
    objectFit="cover"
    backgroundSize="cover"
    backgroundPosition="center"
    rounded={rounded}
    justifyContent="center"
    width={imageSize}
    height={imageSize}
    backgroundImage={`url(${image || imagePlaceholder})`}
    {...rest}
  >
    {isLoading && <Spinner size="lg" speed="0.65s" color="gray" />}
  </Flex>
);

const ImageUploadArea =
  ({ children }) =>
  (
    image,
    // imagePlaceholder = placeholder,
    isLoading,
    onDrop,
    onDelete,
    setEditor,
    loading,
    hasError,
  ) => {
    return (
      <Flex
        flexShrink={0}
        align="center"
        justifyContent="center"
        borderWidth="2xs"
        borderColor="neutral.500"
        rounded="lg"
      >
        {image ? (
          <Stack align="center" spacing={[5, 6]} p={[6, 8]}>
            {hasError === false ? (
              <Box pt={2}>
                <Alert rounded="lg" status="error">
                  <AlertIcon />
                  Image is blurry or we couldn't detect a face. Please upload a
                  better photo.
                </Alert>
              </Box>
            ) : null}

            <ImagePreview
              image={image}
              isLoading={loading}
              imagePlaceholder={placeholder}
              onClick={() => setEditor(true)}
            />

            {hasError !== false && (
              <Text fontWeight="medium">This is how clients will see you</Text>
            )}

            <Flex direction={["column", "row"]}>
              {hasError === false ? null : (
                <Button
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<EditIcon />}
                  onClick={() => setEditor(true)}
                  mr={[0, 3]}
                  mb={[3, 0]}
                  mt={2}
                >
                  Edit photo
                </Button>
              )}

              <Dropzone
                accept="image/jpeg, image/png"
                multiple={false}
                onDrop={onDrop}
                onDragEnter={() => console.log("Drag enter")}
                onDropRejected={() => console.log("Drop Rejected")}
                onDropAccepted={() => console.log("Drop accepted")}
                onDragOver={() => console.log("Drag over")}
              >
                {({ getRootProps, getInputProps }) => {
                  return (
                    <Stack align="center" cursor="pointer" {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Button
                        variant="outline"
                        mr={[0, 3]}
                        mb={[3, 0]}
                        colorScheme="whatsapp"
                        onClick={(e) => e.preventDefault()}
                      >
                        Change photo
                      </Button>
                    </Stack>
                  );
                }}
              </Dropzone>
            </Flex>
          </Stack>
        ) : (
          <Dropzone
            // {...{ minSize, maxSize }}
            accept="image/jpeg, image/png"
            multiple={false}
            onDrop={onDrop}
            onDragEnter={() => console.log("Drag enter")}
            onDropRejected={() => console.log("Drop Rejected")}
            onDropAccepted={() => console.log("Drop accepted")}
            onDragOver={() => console.log("Drag over")}
          >
            {({ getRootProps, getInputProps }) => {
              return (
                <Stack
                  align="center"
                  spacing={3}
                  p={[6, 8]}
                  cursor="pointer"
                  {...getRootProps()}
                >
                  {hasError === false ? (
                    <Box pt={2}>
                      <Alert rounded="lg" status="error">
                        <AlertIcon />
                        Image is blurry. Please upload a higher quality image
                        with a visible face.
                      </Alert>
                    </Box>
                  ) : null}
                  <input {...getInputProps()} />
                  <ImagePreview
                    image={image}
                    imagePlaceholder={placeholder}
                    isLoading={isLoading}
                  />

                  <Box className="text-and-button">
                    {children}
                    <XButton
                      onClick={(e) => e.preventDefault()}
                      colorScheme="blue"
                      size="lg"
                      variant="outline"
                      leftIcon={"upload"}
                      marginTop="8px"
                    >
                      Upload best photo
                    </XButton>
                  </Box>
                </Stack>
              );
            }}
          </Dropzone>
        )}
      </Flex>
    );
  };

export const ImageUploader: React.FC<{
  image: any;
  onChange: any;
  loading: boolean;
  hasError: boolean;
  render: any;
  [key: string]: any;
}> = observer(({ image, onChange, loading, render, hasError }) => {
  return (
    <ImageUploadHandler
      hasError={hasError}
      onChange={onChange}
      loading={loading}
      key={image}
      image={image}
      render={render}
    />
  );
});

ImageUploader.defaultProps = {
  label: "",
  children: <Text>Click to upload image or drag image here</Text>,
  onChange: () => {},
};

export const ImageInput: React.FC<{
  placeholder: string;
  value: any;
  render?: any;
  onChange: (params: any) => Promise<string>;
  loading: boolean;
  hasError: boolean | null;
  imagePlaceholder?: string;
  [key: string]: any;
}> = observer(
  ({
    placeholder,
    value,
    render = ImageUploadArea,
    onChange,
    loading,
    hasError,
    ...props
  }) => {
    return (
      <ImageUploader
        image={value}
        onChange={onChange}
        loading={loading}
        render={render(props)}
        hasError={hasError}
      >
        {placeholder && <Text>{placeholder}</Text>}
      </ImageUploader>
    );
  },
);

const IDInformation = [
  {
    title: "What we accept",
    color: "green.500",
    IDTypes: [
      "International passport",
      "National ID or NIMS",
      "Driver's License",
      "Voter's Card",
    ],
  },
  {
    title: "We don't accept",
    color: "red.500",
    IDTypes: ["School IDs", "Work or Union IDs", "Blurry IDs", "Other IDs"],
  },
];

export const VerificationIdentity: React.FC<{
  store: IIdentityStore;
  onSubmit: any;
  formHeader: string;
  lockedDescription: string;
  formSummary?: any;
  completed?: boolean;
  loading?: boolean;
  isCollapsed?: boolean;
  buttonText?: string;
  buttonIsDisabled?: boolean;
  displayType?: string;
  label?: string;
  includeProfilePhoto?: boolean;
  includeValidId?: boolean;
  buttonProps?: any;
  afterUpload?: any;
  onSkip?: any;
  displayNotice?: boolean;
  imagePlaceholder?: string;
}> = observer(
  ({
    store,
    onSubmit,
    buttonText,
    buttonIsDisabled,
    loading,
    includeProfilePhoto = true,
    includeValidId = true,
    buttonProps = {},
    afterUpload,
    onSkip,
    displayNotice = true,
    imagePlaceholder = placeholder,
  }) => {
    const [hideSubmit, setHideSubmit] = useState(false);
    return (
      <Stack spacing={[8, 12]} width="100%">
        {includeProfilePhoto && (
          <Box>
            <Stack id="form-header-section" mb={4}>
              <Text fontWeight="bold" fontSize={["3xl", "4xl"]}>
                Upload profile photo
              </Text>
              <Text color="gray.700">
                Add a professional photo to your profile. Ensure to upload your
                best photo as clients will see this when selecting tutors.
                Great, high-quality profile photos can get you up to 3x more
                bookings.
              </Text>
            </Stack>

            <ImageInput
              value={getSnapshot(store).profilePhoto}
              placeholder="Upload Image Placeholder"
              label="Upload Image"
              loading={store.loading}
              imagePlaceholder={imagePlaceholder}
              hasError={store.goodQuality && store.has_face}
              onChange={
                afterUpload
                // if(x.size > 2097152){
                //   displayToast({
                //     status: "error",
                //     title: "Large file size",
                //     duration: 4000,
                //     position: "top",
                //     description:
                //       "Image size is too large. Upload image less than 2MB",
                //   });
                //   afterUpload()
                //   return Promise.reject("File size is too large. Upload less than 2MB")
                // }else{
                //   return store.onUploadPhoto(x).then((r) => {
                //     if (afterUpload) {
                //       afterUpload();
                //     }
                //   })}

                // }
              }
            />
          </Box>
        )}

        {includeValidId && (
          <Stack spacing={6} className="Upload-ID">
            <Stack>
              <Text fontWeight="bold" fontSize={["3xl", "4xl"]}>
                Upload a valid ID
              </Text>
              <Text color="gray.700">
                Confirm your identity by uploading a clear copy of any of the
                accepted government-issued IDs below. This helps us verify your
                identity and build trust with clients.
              </Text>
            </Stack>

            {displayNotice && (
              <Box>
                <Alert status="warning" rounded="md">
                  <AlertIcon />
                  Your face and name must be visible else your ID will be
                  rejected
                </Alert>
              </Box>
            )}
            {store.isIdVerified ? (
              <Stack
                p={[5, 6]}
                spacing={5}
                align="center"
                borderWidth="2xs"
                borderColor="neutral.500"
                rounded="lg"
              >
                <Icon name="identity" fontSize="3xl" />
                <Text fontWeight="medium">Your ID has been verified</Text>
              </Stack>
            ) : (
              <Stack
                p={[6, 10]}
                spacing={[8, 10]}
                borderWidth="2xs"
                rounded="lg"
                borderColor="neutral.500"
              >
                <Grid
                  templateColumns={["none", "1.5fr 1fr"]}
                  rowGap={6}
                  columnGap={[3, 5]}
                >
                  {IDInformation.map((item, idx) => (
                    <Stack key={item.title}>
                      <Text fontWeight="extrabold" color={item.color}>
                        {item.title}
                      </Text>

                      <List spacing={3}>
                        {item.IDTypes.map((IDs) => (
                          <ListItem key={IDs}>
                            <ListIcon
                              as={idx === 0 ? CheckCircleIcon : CloseIcon}
                              color={item.color}
                            />
                            {IDs}
                          </ListItem>
                        ))}
                      </List>
                    </Stack>
                  ))}
                </Grid>

                <Upload
                  allowMultiple={false}
                  afterDrop={afterUpload}
                  store={store.uploadStore}
                >
                  <XButton
                    size="lg"
                    isFullWidth={useBreakpointValue({ base: true, md: false })}
                    alignSelf="center"
                    leftIcon={"upload"}
                    colorScheme="blue"
                  >
                    Click to upload ID
                  </XButton>
                </Upload>
                {store.uploadStore.files.length == 0 && (
                  <>
                    <Divider />

                    <Stack spacing={6}>
                      <Text>
                        If you're experiencing difficulties or can't upload your
                        ID, please skip this step for now. We'll ask you to send
                        it to us later.
                      </Text>
                      <Button
                        colorScheme="blue"
                        alignSelf="start"
                        variant="link"
                        isLoading={loading}
                        loadingText="Skipping for now"
                        onClick={() => {
                          setHideSubmit(true);
                          if (onSkip) {
                            onSkip();
                          }
                        }}
                      >
                        Skip for now
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        )}
        <Box width="full">
          {!hideSubmit && (
            <XButton
              alignSelf="flex-start"
              size="lg"
              isFullWidth
              colorScheme="blue"
              loadingText="Saving details"
              isDisabled={buttonIsDisabled}
              isLoading={loading}
              onClick={() => onSubmit()}
            >
              Save and continue
            </XButton>
          )}
        </Box>
      </Stack>
    );
  },
);

export default VerificationIdentity;
